import React, { useCallback, useMemo, useRef, useState } from 'react';
import { MarkersAPI } from '../../../api/MarkerEndpoints';
import { WEEKDAY_KEYS } from './markerCsvFormat';
import './BulkCsvUploadModal.css';

// The file layout (row 1 = technical column names, row 2 = descriptions, data
// from row 3) is defined by the xlsx templates under public/ and by
// markerCsvFormat.js; the dashboard's CSV export writes the same columns.

// ── CSV pre-processing (fixes common Excel export issues) ────────────────────

function parseCsvLine(line, delim) {
  const fields = [];
  let field = '';
  let inQuotes = false;
  let i = 0;
  while (i < line.length) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') { field += '"'; i += 2; }
      else { inQuotes = !inQuotes; i++; }
    } else if (ch === delim && !inQuotes) {
      fields.push(field); field = ''; i++;
    } else {
      field += ch; i++;
    }
  }
  fields.push(field);
  return fields;
}

const NUMERIC_FIELDS = ['latitude','longitude','entryPrice','minAge','drinkPriceIndicator','boxOfficePrice'];
const WEEKDAYS_ORDER = WEEKDAY_KEYS;

function normalizeNumeric(val) {
  // Accept both "48.202" and "48,202" (German decimal comma)
  const s = (val || '').trim().replace(',', '.');
  return s;
}

function isDescriptionRow(fields, headers) {
  // The template's second row explains each column. It is recognised by a type
  // cell that is neither Event nor Club (any casing, so a hand-written "event"
  // stays a data row) or by prose in a numeric column. The type column is looked
  // up by name: the dashboard's export puts `id` in front of it.
  const typeIdx = Math.max(0, headers.indexOf('type'));
  const type = fields[typeIdx]?.trim();
  if (type && !/^(event|club)$/i.test(type)) return true;
  for (const name of NUMERIC_FIELDS) {
    const idx = headers.indexOf(name);
    if (idx < 0 || idx >= fields.length) continue;
    const val = normalizeNumeric(fields[idx]);
    if (val && isNaN(Number(val))) return true;
  }
  return false;
}

const encodeField = (f) => {
  const s = String(f ?? '');
  return s.includes(',') || s.includes('"') || s.includes('\n')
    ? '"' + s.replace(/"/g, '""') + '"'
    : s;
};

const DRESSCODE_MAP = {
  casual: 'egal', casual_attire: 'egal', normal: 'egal', locker: 'egal',
  formal: 'elegant', smart: 'elegant', 'smart casual': 'elegant',
  sexy: 'kinky', fetish: 'kinky',
  sport: 'sportlich', sports: 'sportlich', athletic: 'sportlich',
  office: 'business', suit: 'business',
  dress_up: 'costume', verkleidung: 'costume',
};

const VALID_DRESSCODES = new Set(['egal','elegant','fancy','kinky','business','sportlich','costume']);

const DESC_FIELDS = new Set(['descriptionGerman', 'descriptionEnglish']);
const DESC_MAX = 1000;

function fixField(f, headerName = '') {
  if (f.toLowerCase() === 'wahr')   return 'true';
  if (f.toLowerCase() === 'falsch') return 'false';
  if (f.length >= 2 && f.startsWith('"') && f.endsWith('"')) f = f.slice(1, -1);
  // Truncate descriptions to DB limit
  if (DESC_FIELDS.has(headerName) && f.length > DESC_MAX) return f.slice(0, DESC_MAX);
  // Fix German decimal comma → dot for numeric fields
  if (NUMERIC_FIELDS.includes(headerName) && /^-?\d+,\d+$/.test(f.trim())) return f.trim().replace(',', '.');
  // Normalize dressCode: map aliases, reject unknown → fallback egal
  if (headerName === 'dressCode' && f) {
    const lower = f.trim().toLowerCase();
    if (VALID_DRESSCODES.has(lower)) return lower;
    return DRESSCODE_MAP[lower] || 'egal';
  }
  // Convert comma-separated genres to pipe-separated (backend splits on '|')
  if (headerName === 'musicGenre' && f.includes(',')) {
    return f.split(',').map(s => s.trim()).filter(Boolean).join('|');
  }
  // Strip spaces from specialType
  if (headerName === 'specialType' && f.includes(',')) {
    return f.split(',').map(s => s.trim()).join(',');
  }
  // Strip leading @ from Instagram handles
  if (headerName === 'instagramName' && f.startsWith('@')) return f.slice(1);
  // A German-locale Excel saves an ISO date back as 20.06.2026; the backend wants 2026-06-20.
  // Other locales write 20/06/2026.
  if (headerName === 'eventDate') {
    const m = /^(\d{1,2})[./](\d{1,2})[./](\d{4})$/.exec(f.trim());
    if (m) return `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
  }
  // A German Excel reads 48.2472 as the whole number 482472 and saves it with a thousands dot
  // as "482.472". Coordinates of 100 and more cannot be right for Vienna (48.x, 16.x), so the
  // digits are put back with two before the decimal point.
  if ((headerName === 'latitude' || headerName === 'longitude') && /^\d{1,3}(\.\d{3})+$/.test(f.trim())) {
    const digits = f.trim().replace(/\./g, '');
    if (Number(f.trim()) >= 100) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  }
  // Normalize time: HH:MM → HH:MM:SS
  if (/^\d{2}:\d{2}$/.test(f.trim())) return f.trim() + ':00';
  return f;
}

// Maps descriptive German/English column headers → technical field names
const HEADER_ALIASES = {
  // type
  'typ': 'type', 'typ: event oder club': 'type',
  // title
  'name der veranstaltung': 'title', 'name des clubs': 'title', 'name': 'title',
  // descriptions
  'deutsche beschreibung': 'descriptionGerman', 'beschreibung deutsch': 'descriptionGerman',
  'englische beschreibung': 'descriptionEnglish', 'beschreibung englisch': 'descriptionEnglish',
  // music
  'musikgenres': 'musicGenre', 'music genres': 'musicGenre', 'genre': 'musicGenre',
  // image
  'instagram-post-url': 'image', 'instagram bild-url': 'image', 'bild': 'image',
  // instagram
  'instagram name': 'instagramName', 'instagram handle': 'instagramName', 'instagram name des event/club instagram accounts ohne @': 'instagramName',
  // address
  'adresse': 'address', 'adresse in anführungszeichen bei csv': 'address',
  // lat/lon
  'breitengrad': 'latitude', 'latitude': 'latitude',
  'längengrad': 'longitude', 'longitude': 'longitude',
  // price / age
  'regulärer eintritt': 'entryPrice', 'eintrittspreis': 'entryPrice', 'entry price': 'entryPrice',
  'mindestalter': 'minAge', 'min age': 'minAge',
  // dresscode
  'dresscode': 'dressCode',
  // drink price
  'drink-preis-index': 'drinkPriceIndicator', 'drink preis index': 'drinkPriceIndicator', 'drinkpreisindikator': 'drinkPriceIndicator',
  // special type
  'veranstaltungstyp': 'specialType', 'special type': 'specialType',
  // event-only
  'datum': 'eventDate', 'event datum': 'eventDate',
  'startzeit': 'startTime', 'endzeit': 'endTime',
  'ticket-link': 'ticketUrl', 'ticket link': 'ticketUrl',
  'host-code': 'hostCode', 'host code': 'hostCode',
  'abendkasse preis': 'boxOfficePrice',
  'abendkasse verfügbarkeit': 'boxOfficeAvailability',
  'awareness-team': 'hasAwarenessTeam',
  // weekdays
  'montag öffnung': 'mondayOpen',   'montag schließung': 'mondayClose',
  'dienstag öffnung': 'tuesdayOpen', 'dienstag schließung': 'tuesdayClose',
  'mittwoch öffnung': 'wednesdayOpen','mittwoch schließung': 'wednesdayClose',
  'donnerstag öffnung': 'thursdayOpen','donnerstag schließung': 'thursdayClose',
  'freitag öffnung': 'fridayOpen',   'freitag schließung': 'fridayClose',
  'samstag öffnung': 'saturdayOpen', 'samstag schließung': 'saturdayClose',
  'sonntag öffnung': 'sundayOpen',   'sonntag schließung': 'sundayClose',
};

function resolveHeader(raw) {
  const key = raw.trim().toLowerCase().split('(')[0].trim(); // drop parenthetical hints
  return HEADER_ALIASES[key] || raw.trim();
}

// The event template names up to three acts in separate columns (artist1Name,
// artist1Instagram, artist1SpotifyId, …). The backend reads a single `artists` cell in
// the form `Name;instagram=handle;spotifyId=id|Name2…`, so the columns are folded here.
const ARTIST_COLUMN = /^artist(\d+)(Name|Instagram|SpotifyId)$/i;

function encodeArtistEntry(name, instagram, spotifyId) {
  // `|` separates acts and `;` their attributes, so neither may survive inside a value.
  const clean = (v) => String(v ?? '').replace(/[|;]/g, ' ').replace(/\s+/g, ' ').trim();
  const handle = clean(instagram).replace(/^@+/, '');
  const artistName = clean(name) || handle;
  if (!artistName) return null;
  const parts = [artistName];
  if (handle) parts.push(`instagram=${handle}`);
  const id = clean(spotifyId);
  if (id) parts.push(`spotifyId=${id}`);
  return parts.join(';');
}

export function preprocessCsvText(raw) {
  // 1. Strip UTF-8 BOM
  const text = raw.replace(/^﻿/, '');

  // 2. Detect delimiter
  const firstLine = text.split(/\r?\n/)[0];
  const delim = (firstLine.match(/;/g) || []).length > (firstLine.match(/,/g) || []).length ? ';' : ',';

  // 3. Parse all non-empty lines
  const parsed = text.split(/\r?\n/)
    .map(l => l.trim())
    .filter(Boolean)
    .map(l => parseCsvLine(l, delim));

  if (parsed.length === 0) return '';

  // 4. Parse header and resolve descriptive names → technical field names
  const origHeaders = parsed[0].map(h => h.trim());
  const headers = origHeaders.map(resolveHeader);

  // 5. Detect separate weekday columns and build config
  const weekdayConfig = WEEKDAYS_ORDER.map(d => ({
    label: d.charAt(0).toUpperCase() + d.slice(1),
    openIdx:  headers.indexOf(`${d}Open`),
    closeIdx: headers.indexOf(`${d}Close`),
  })).filter(w => w.openIdx >= 0 || w.closeIdx >= 0);

  const weekdayIndices = new Set(weekdayConfig.flatMap(w => [w.openIdx, w.closeIdx].filter(i => i >= 0)));
  const hasSeparateWeekdays = weekdayIndices.size > 0;

  // 5b. Detect the template's per-artist columns. A file that already carries the
  //     backend's `artists` column keeps it; the backend ignores the extra columns then.
  const artistConfig = [];
  if (!headers.includes('artists')) {
    headers.forEach((h, idx) => {
      const m = ARTIST_COLUMN.exec(h);
      if (!m) return;
      const n = Number(m[1]);
      let slot = artistConfig.find(a => a.n === n);
      if (!slot) { slot = { n, nameIdx: -1, instagramIdx: -1, spotifyIdx: -1 }; artistConfig.push(slot); }
      const key = m[2].toLowerCase();
      if (key === 'name') slot.nameIdx = idx;
      else if (key === 'instagram') slot.instagramIdx = idx;
      else slot.spotifyIdx = idx;
    });
    artistConfig.sort((a, b) => a.n - b.n);
  }
  const artistIndices = new Set(artistConfig.flatMap(a => [a.nameIdx, a.instagramIdx, a.spotifyIdx].filter(i => i >= 0)));
  const hasSeparateArtists = artistIndices.size > 0;

  const foldedIndices = new Set([...weekdayIndices, ...artistIndices]);

  // 6. Build output headers
  const outHeaders = [
    ...headers.filter((_, i) => !foldedIndices.has(i)),
    ...(hasSeparateWeekdays ? ['weekdays'] : []),
    ...(hasSeparateArtists ? ['artists'] : []),
  ];

  const result = [outHeaders.map(encodeField).join(',')];

  // 7. Process data rows
  for (let i = 1; i < parsed.length; i++) {
    const raw = parsed[i];

    if (isDescriptionRow(raw, headers)) continue;

    const fields = raw.map((f, idx) => fixField(f, headers[idx] || ''));
    const outFields = fields.filter((_, idx) => !foldedIndices.has(idx));

    // Convert separate weekday columns → weekdays encoded string
    if (hasSeparateWeekdays) {
      outFields.push(weekdayConfig
        .map(w => {
          const open  = w.openIdx  >= 0 ? fields[w.openIdx]?.trim()  : '';
          const close = w.closeIdx >= 0 ? fields[w.closeIdx]?.trim() : '';
          return open && close ? `${w.label}@${open}-${close}` : null;
        })
        .filter(Boolean)
        .join('|'));
    }

    // Fold artistN columns → artists encoded string
    if (hasSeparateArtists) {
      outFields.push(artistConfig
        .map(a => encodeArtistEntry(
          a.nameIdx >= 0 ? fields[a.nameIdx] : '',
          a.instagramIdx >= 0 ? fields[a.instagramIdx] : '',
          a.spotifyIdx >= 0 ? fields[a.spotifyIdx] : '',
        ))
        .filter(Boolean)
        .join('|'));
    }

    result.push(outFields.map(encodeField).join(','));
  }

  return result.join('\n');
}

async function readFileText(file) {
  const buffer = await file.arrayBuffer();
  const bytes = new Uint8Array(buffer);

  // UTF-8 BOM (EF BB BF) → definitely UTF-8
  if (bytes[0] === 0xEF && bytes[1] === 0xBB && bytes[2] === 0xBF) {
    return new TextDecoder('utf-8').decode(buffer);
  }

  // Try strict UTF-8; if it throws, fall back to Windows-1252 (Excel default for DE/AT)
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(buffer);
  } catch {
    return new TextDecoder('windows-1252').decode(buffer);
  }
}

async function preprocessFile(file) {
  const raw = await readFileText(file);
  const cleaned = preprocessCsvText(raw);
  // Always write back as UTF-8 so the backend receives clean text
  return new File([cleaned], file.name, { type: 'text/csv;charset=utf-8' });
}

export default function BulkCsvUploadModal({ open, onClose, onUploaded }) {
  const [file, setFile] = useState(null);
  const [drag, setDrag] = useState(false);
  const [dryRun, setDryRun] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);

  const fileInputRef = useRef(null);
  const hasErrors = useMemo(() => (result?.errors?.length || 0) > 0, [result]);

  const reset = useCallback(() => {
    setFile(null); setDryRun(false); setUploading(false);
    setResult(null); setErr(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const pickFile = useCallback((e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setErr(null); setResult(null); setFile(f);
  }, []);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDrag(false);
    const f = e.dataTransfer.files?.[0];
    if (!f) return;
    setErr(null); setResult(null); setFile(f);
  }, []);

  const upload = useCallback(async () => {
    if (!file) { setErr('Bitte eine CSV-Datei auswählen.'); return; }
    if (!file.name.toLowerCase().endsWith('.csv')) { setErr('Nur .csv Dateien sind erlaubt.'); return; }
    try {
      setUploading(true); setErr(null); setResult(null);
      const cleanFile = await preprocessFile(file);
      const res = await MarkersAPI.uploadMarkersCsvBulk(cleanFile, { dryRun });
      setResult(res);
      if (!dryRun && typeof onUploaded === 'function') await onUploaded(res);
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || 'Bulk Upload fehlgeschlagen.';
      setErr(msg);
    } finally {
      setUploading(false);
    }
  }, [file, dryRun, onUploaded]);

  const handleClose = useCallback(() => { reset(); onClose?.(); }, [onClose, reset]);

  if (!open) return null;

  return (
    <div className="csv-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) handleClose(); }}>
      <div className="csv-dialog">

        {/* Header */}
        <div className="csv-header">
          <div className="csv-header-left">
            <div className="csv-header-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="17 8 12 3 7 8"/>
                <line x1="12" y1="3" x2="12" y2="15"/>
              </svg>
            </div>
            <div>
              <h3 className="csv-header-title">Bulk CSV Upload</h3>
              <p className="csv-header-sub">Clubs und Events gemischt aus einer .csv Datei importieren</p>
            </div>
          </div>
          <button className="csv-close" type="button" onClick={handleClose} aria-label="Schließen">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round" width="16" height="16" aria-hidden="true">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="csv-body">

          {/* Drop zone */}
          <div
            className="csv-dropzone"
            data-drag={drag}
            data-file={!!file}
            onDrop={onDrop}
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              onChange={pickFile}
              style={{ display: 'none' }}
              aria-hidden="true"
            />

            {file ? (
              <>
                <div className="csv-drop-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                    strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
                    <path d="M20 6L9 17l-5-5"/>
                  </svg>
                </div>
                <span className="csv-file-name">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
                    strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                  </svg>
                  {file.name}
                </span>
                <span className="csv-drop-sub">Klicken um eine andere Datei zu wählen</span>
              </>
            ) : (
              <>
                <div className="csv-drop-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                    strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="17 8 12 3 7 8"/>
                    <line x1="12" y1="3" x2="12" y2="15"/>
                  </svg>
                </div>
                <span className="csv-drop-title">Datei hierher ziehen</span>
                <span className="csv-drop-sub">oder klicken zum Auswählen — <code>.csv</code></span>
              </>
            )}
          </div>

          {/* Templates */}
          <div className="csv-templates">
            <span className="csv-templates-label">Vorlage herunterladen:</span>
            <a className="csv-tpl-btn" href={`${process.env.PUBLIC_URL}/event-creation-template.xlsx`} download="event-creation-template.xlsx">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round" width="11" height="11">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              Event Vorlage (.xlsx)
            </a>
            <a className="csv-tpl-btn" href={`${process.env.PUBLIC_URL}/club-creation-template.xlsx`} download="club-creation-template.xlsx">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round" width="11" height="11">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              Club Vorlage (.xlsx)
            </a>
          </div>

          {/* Controls */}
          <div className="csv-controls">
            <label className="csv-dry-label">
              <input
                type="checkbox"
                checked={dryRun}
                onChange={(e) => setDryRun(e.target.checked)}
                disabled={uploading}
              />
              Dry run — nur prüfen, nicht speichern
            </label>

            <div className="csv-btn-group">
              <button className="csv-btn" type="button" onClick={reset} disabled={uploading}>
                Zurücksetzen
              </button>
              <button className="csv-btn csv-btn-primary" type="button" onClick={upload}
                disabled={uploading || !file}>
                {uploading ? (
                  <>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                      strokeLinecap="round" strokeLinejoin="round" width="13" height="13"
                      style={{ animation: 'csv-spin 0.9s linear infinite' }}>
                      <path d="M3 12a9 9 0 1 0 9-9"/>
                    </svg>
                    Lade hoch…
                  </>
                ) : (
                  <>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
                      strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                      <polyline points="17 8 12 3 7 8"/>
                      <line x1="12" y1="3" x2="12" y2="15"/>
                    </svg>
                    Hochladen
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Error banner */}
          {err && <div className="csv-error">{err}</div>}

          {/* Result */}
          {result && (
            <div className="csv-result">
              <div className="csv-stats">
                <span className="csv-stat csv-stat--ok">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
                    strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
                    <path d="M20 6L9 17l-5-5"/>
                  </svg>
                  {result.createdCount ?? 0} erstellt
                </span>
                {(result.failedCount ?? 0) > 0 && (
                  <span className="csv-stat csv-stat--fail">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
                      strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
                      <path d="M18 6L6 18M6 6l12 12"/>
                    </svg>
                    {result.failedCount} fehlgeschlagen
                  </span>
                )}
                <span className="csv-stat csv-stat--neutral">
                  {result.createdCount ?? 0} / {(result.createdCount ?? 0) + (result.failedCount ?? 0)} gesamt
                </span>
              </div>

              {hasErrors && (
                <div>
                  <div className="csv-err-title">Fehler</div>
                  <div className="csv-err-wrap">
                    <table className="csv-err-table">
                      <thead>
                        <tr>
                          <th>Zeile</th>
                          <th>Feld</th>
                          <th>Meldung</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.errors.map((e, idx) => (
                          <tr key={idx}>
                            <td className="mono">{e.row}</td>
                            <td className="mono">{e.field ?? '—'}</td>
                            <td>{e.message}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="csv-hint-block">
                    <p className="csv-hint"><strong>Zeile 1:</strong> technische Feldnamen — nicht löschen, Backend liest diese</p>
                    <p className="csv-hint"><strong>Zeile 2:</strong> Beschreibungen — vor dem Upload löschen</p>
                    <p className="csv-hint"><strong>musicGenre:</strong> kommagetrennt — <code>Techno,House,Rap</code></p>
                    <p className="csv-hint"><strong>Artists:</strong> <code>artist1Name</code> · <code>artist1Instagram</code> · <code>artist1SpotifyId</code> (bis artist3) — bekannte Artists werden wiederverwendet, neue angelegt; ohne Spotify-ID wird der Name auf Spotify gesucht</p>
                    <p className="csv-hint"><strong>Öffnungszeiten:</strong> <code>fridayOpen</code> / <code>fridayClose</code> … Format <code>HH:MM:SS</code>, leer = geschlossen</p>
                    <p className="csv-hint"><strong>boxOfficeAvailability:</strong> <code>UNLIMITED</code> · <code>LIMITED</code> · <code>PRESALE_ONLY</code></p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
